import { useCreatorResolver } from "@/organization"
import { TemplateDependencyDeleteDialog } from "@/template-dependencies"
import { Download } from "lucide-react"

import {
  ResourceHeaderActions,
  type ResourceMenuEntry,
} from "@/components/resource-header/ResourceHeaderActions"
import { useQueryParams } from "@/hooks/useQueryParams"

import { useTestSetBreadcrumb } from "./useTestSetBreadcrumb"

interface TestSetBreadcrumbActionsProps {
  /** Gate for the restore note in the delete dialog, resolved by the route. */
  recentlyDeletedEnabled?: boolean
}

export const TestSetBreadcrumbActions = ({
  recentlyDeletedEnabled = false,
}: TestSetBreadcrumbActionsProps) => {
  const breadcrumb = useTestSetBreadcrumb()

  if (!breadcrumb.testSetId) {
    return null
  }

  return (
    <PersistedTestSetBreadcrumbActions
      breadcrumb={breadcrumb}
      recentlyDeletedEnabled={recentlyDeletedEnabled}
    />
  )
}

interface PersistedTestSetBreadcrumbActionsProps {
  breadcrumb: ReturnType<typeof useTestSetBreadcrumb>
  recentlyDeletedEnabled: boolean
}

const PersistedTestSetBreadcrumbActions = ({
  breadcrumb,
  recentlyDeletedEnabled,
}: PersistedTestSetBreadcrumbActionsProps) => {
  const queryParams = useQueryParams()
  const { resolveCreator } = useCreatorResolver()
  const {
    testSetId,
    testSetMetadata,
    isCloning,
    isDeleting,
    isRenaming,
    handleDownload,
    handleCloneTestSet,
    handleDeleteTestSet,
    handleConfirmDelete,
    handleCancelDelete,
    isDeleteModalOpen,
    isPersisted,
    handleOpenVersionHistory,
    recordsIsLoading,
    recordsIsError,
  } = breadcrumb

  const testSetName = testSetMetadata?.metadata?.display_name

  const isDownloadBlocked = recordsIsLoading || recordsIsError
  const additionalActions: ResourceMenuEntry[] = [
    {
      label: recordsIsLoading ? "Download (loading…)" : "Download",
      icon: <Download />,
      onSelect: handleDownload,
      disabled: isDownloadBlocked,
      "data-testid": "test-set-download-menu-item",
    },
  ]

  return (
    <>
      <ResourceHeaderActions
        versionHistory={{ onOpen: handleOpenVersionHistory }}
        additionalActions={additionalActions}
        resourceId={isPersisted ? testSetId : undefined}
        resourceUrl={
          isPersisted
            ? queryParams.getHref(
                `/test-sets/${testSetId}`,
                String(queryParams.get("slug") ?? ""),
              )
            : undefined
        }
        attribution={
          isPersisted && testSetMetadata?.metadata
            ? {
                createdAt: testSetMetadata.metadata.created_at,
                createdBy: testSetMetadata.metadata.created_by_id
                  ? resolveCreator(testSetMetadata.metadata.created_by_id)
                  : undefined,
                updatedAt: testSetMetadata.metadata.last_updated_at,
              }
            : undefined
        }
        duplicate={{
          onDuplicate: () => {
            void handleCloneTestSet()
          },
          pending: isCloning,
          disabled: isRenaming,
          "data-testid": "test-set-duplicate-menu-item",
        }}
        delete={
          isPersisted
            ? {
                onDelete: handleDeleteTestSet,
                pending: isDeleting,
                disabled: isRenaming,
                "data-testid": "test-set-delete-menu-item",
              }
            : undefined
        }
      />

      <TemplateDependencyDeleteDialog
        open={isDeleteModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleCancelDelete()
          }
        }}
        onConfirm={() => {
          void handleConfirmDelete()
        }}
        title="Delete Test Set"
        resourceId={testSetId}
        resourceKind="testSet"
        prompt={
          testSetName ? (
            <>
              Are you sure you want to delete <strong>{testSetName}</strong>?
            </>
          ) : (
            "Are you sure you want to delete this test set?"
          )
        }
        note={
          recentlyDeletedEnabled
            ? "You can restore it from Recently Deleted."
            : undefined
        }
        isDeleting={isDeleting}
        confirmTestId="test-set-delete-confirm-button"
      />
    </>
  )
}
